import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useGetExpeditionFinalizedDayCountsBo } from "@/api/expedition.api";
import { ExpeditionDayCountBo, GetAllExpeditionBoDto } from "@/types/api/api-types";
import { cn } from "@/lib/utils";

// "classic" (blue) shows every finalized expedition of the day. "blocked" (orange) restricts
// the chart to blocked expeditions.
type ChartMode = "classic" | "blocked";

type Props = {
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  selectedDay: Date | null;
  onSelectedDayChange: (day: Date | null) => void;
  isActive: boolean;
  onToggle: () => void;
  // Extra query params merged into the day-counts request, e.g. to restrict the chart
  // to a single delivery status group (blocked-only page).
  extraFilter?: Partial<GetAllExpeditionBoDto>;
  mode?: ChartMode;
};

const STATUS_COLORS = {
  deliveredCount: "#16a34a",
  ongoingCount: "#eab308",
  blockedCount: "#f97316",
  failedCount: "#ef4444",
} as const;

const useStatusLabels = (): Record<keyof typeof STATUS_COLORS, string> => {
  const { t } = useTranslation("boExpeditions");
  return useMemo(
    () => ({
      ongoingCount: t("chartDayPicker.statuses.ongoing"),
      deliveredCount: t("chartDayPicker.statuses.delivered"),
      blockedCount: t("chartDayPicker.statuses.blocked"),
      failedCount: t("chartDayPicker.statuses.failed"),
    }),
    [t],
  );
};

const EMPTY_DAY_COUNT: ExpeditionDayCountBo = {
  date: "",
  ongoingCount: 0,
  deliveredCount: 0,
  blockedCount: 0,
  failedCount: 0,
};

function getDayTotal(counts: ExpeditionDayCountBo): number {
  return counts.ongoingCount + counts.deliveredCount + counts.blockedCount + counts.failedCount;
}

function getModeTotal(counts: ExpeditionDayCountBo, mode: ChartMode): number {
  switch (mode) {
    case "blocked":
      return counts.blockedCount;
    case "classic":
      return getDayTotal(counts);
  }
}

function getModeAccent(mode: ChartMode) {
  switch (mode) {
    case "blocked":
      return {
        toggleActive: "border-[#f97316] bg-[rgba(249,115,22,0.1)] text-[#f97316]",
        totalText: "text-orange-700",
        todayRing: "ring-2 ring-[#f97316]/40 ring-offset-1",
        selectedRing: "bg-[#f97316]/10 ring-2 ring-[#f97316] ring-offset-1",
        bar: "bg-[#f97316]",
        barSelected: "bg-[#c2410c]",
        numberText: "text-[#f97316]",
      };
    case "classic":
      return {
        toggleActive: "border-[#3562f8] bg-[rgba(53,98,248,0.1)] text-[#3562f8]",
        totalText: "text-blue-700",
        todayRing: "ring-2 ring-[#3562f8]/40 ring-offset-1",
        selectedRing: "bg-[#3562f8]/10 ring-2 ring-[#3562f8] ring-offset-1",
        bar: "bg-[#3562f8]",
        barSelected: "bg-[#1a3fc4]",
        numberText: "text-[#3562f8]",
      };
  }
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

type ChartHeaderProps = {
  monthLabel: string;
  isActive: boolean;
  onToggle: () => void;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  mode: ChartMode;
};

const ChartHeader = ({
  monthLabel,
  isActive,
  onToggle,
  total,
  onPrev,
  onNext,
  mode,
}: ChartHeaderProps) => {
  const { t } = useTranslation("boExpeditions");
  const accent = getModeAccent(mode);
  return (
    <div>
      <div className="mb-4 flex items-center justify-between pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onPrev}
            className="flex h-9 w-9 items-center justify-center rounded-lg border bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={onToggle}
            className={cn(
              "flex h-9 w-38 items-center justify-between rounded-lg border pr-2 pl-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] transition-colors",
              isActive
                ? accent.toggleActive
                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
            )}
          >
            <span className="text-sm font-medium whitespace-nowrap capitalize">{monthLabel}</span>
            {isActive && <X className="h-4 w-4" />}
          </button>
          <button
            onClick={onNext}
            className="flex h-9 w-9 items-center justify-center rounded-lg border bg-white hover:bg-gray-50"
          >
            <ArrowRight className="h-4 w-4 text-gray-600" />
          </button>
          <span className="text-md ml-2 text-gray-500">
            <Trans
              t={t}
              i18nKey={
                mode === "classic"
                  ? "chartDayPicker.finalizedByTeam"
                  : "chartDayPicker.blockedExpeditions"
              }
              count={total}
              components={{ bold: <b className={cn("text-xl", accent.totalText)} /> }}
            />
          </span>
        </div>
      </div>
    </div>
  );
};

type BarTooltipProps = {
  counts: ExpeditionDayCountBo;
  total: number;
  dayDate: Date;
  mode: ChartMode;
};

const BarTooltip = ({ counts, total, dayDate, mode }: BarTooltipProps) => {
  const { t } = useTranslation("boExpeditions");
  const statusLabels = useStatusLabels();
  return (
    <div className="pointer-events-none absolute bottom-[calc(50%+6px)] left-1/2 z-20 flex -translate-x-1/2 flex-col items-center">
      <div className="rounded-lg bg-[#161b55] px-3 py-1.5 text-center whitespace-nowrap">
        <p className="text-sm leading-tight font-bold text-white">{total}</p>
        <p className="text-xs leading-tight font-medium text-white/80">
          {dayDate.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </p>
        {/* Only "classic" mixes several categories in one total — "blocked" already shows
          a single category, so the breakdown would just repeat the total above. */}
        {mode === "classic" && total > 0 && (
          <div className="mt-1 flex flex-col gap-0.5">
            {(["deliveredCount", "ongoingCount", "blockedCount", "failedCount"] as const).map(
              (key) =>
                counts[key] > 0 ? (
                  <div key={key} className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: STATUS_COLORS[key] }}
                    />
                    <span className="text-xs text-white/80">
                      <Trans
                        t={t}
                        i18nKey="chartDayPicker.statusCount"
                        values={{ label: statusLabels[key], amount: counts[key] }}
                        components={{ value: <span className="font-semibold text-white" /> }}
                      />
                    </span>
                  </div>
                ) : null,
            )}
          </div>
        )}
      </div>
      <div
        className="h-[5px] w-[10px] bg-[#161b55]"
        style={{ clipPath: "polygon(0 0, 100% 0, 50% 100%)" }}
      />
    </div>
  );
};

type DayBarProps = {
  counts: ExpeditionDayCountBo;
  total: number;
  dayDate: Date;
  fillHeight: number;
  isSelected: boolean;
  isToday: boolean;
  isHovered: boolean;
  showLabel: boolean;
  onHover: (hovered: boolean) => void;
  onClick: () => void;
  mode: ChartMode;
};

const DayBar = ({
  counts,
  total,
  dayDate,
  fillHeight,
  isSelected,
  isToday,
  isHovered,
  showLabel,
  onHover,
  onClick,
  mode,
}: DayBarProps) => {
  const day = dayDate.getDate();
  const month = dayDate.getMonth() + 1;
  const labelClass = cn(
    "text-[9px] leading-none",
    isSelected || isToday ? "font-bold text-[#161b55]" : "font-medium text-gray-400",
  );

  const accent = getModeAccent(mode);

  return (
    <div
      className="relative flex h-full cursor-pointer flex-col"
      onClick={onClick}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      {isHovered && <BarTooltip counts={counts} total={total} dayDate={dayDate} mode={mode} />}

      <div
        className={cn(
          "relative min-h-0 flex-1 rounded-lg bg-[#e2e8f0]/30",
          isToday && !isSelected && accent.todayRing,
          isSelected && accent.selectedRing,
        )}
      >
        <div
          className={cn(
            "absolute right-0 bottom-0 left-0 rounded-lg transition-all duration-150",
            isSelected ? accent.barSelected : accent.bar,
          )}
          style={{ height: `${fillHeight}%` }}
        >
          <div className="relative h-full w-full">
            <div className="absolute -top-5 flex w-full items-center justify-center">
              {total > 0 && (
                <span className={cn("text-[.875rem] leading-none font-bold", accent.numberText)}>
                  {total}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Label in-flow — always visible on < lg (7 or 16 cols = enough width) */}
      <div className="flex h-7 w-full shrink-0 items-center justify-center">
        <span className={cn(labelClass, "block lg:hidden")}>
          {String(day).padStart(2, "0")}/{String(month).padStart(2, "0")}
        </span>
        {showLabel && (
          <span className={cn(labelClass, "hidden lg:block")}>
            {String(day).padStart(2, "0")}/{String(month).padStart(2, "0")}
          </span>
        )}
      </div>
    </div>
  );
};

export const ChartDayPicker = ({
  currentMonth,
  onMonthChange,
  selectedDay,
  onSelectedDayChange,
  isActive,
  onToggle,
  extraFilter,
  mode = "classic",
}: Props) => {
  const today = new Date();
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const dayCount = daysInMonth(currentMonth);

  const { data: dayCounts } = useGetExpeditionFinalizedDayCountsBo({
    processStatus: ["VALIDATED"],
    finalizedAfter: monthStart.toISOString(),
    finalizedBefore: monthEnd.toISOString(),
    ...extraFilter,
  });

  const days = dayCounts?.data ?? Array.from({ length: dayCount }, () => EMPTY_DAY_COUNT);
  // Computed from the day-by-day data rather than the backend's monthTotalCount/maxTotal —
  // those aggregate every category together, but "blocked" only cares about its own one.
  const total = days.reduce((sum, counts) => sum + getModeTotal(counts, mode), 0);
  const maxTotal = Math.max(1, ...days.map((counts) => getModeTotal(counts, mode)));

  const monthLabel = currentMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const goToPrevMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    if (
      selectedDay?.getMonth() === currentMonth.getMonth() &&
      selectedDay?.getFullYear() === currentMonth.getFullYear()
    ) {
      onSelectedDayChange(null);
    }
    if (isActive) onToggle();
  };

  const goToNextMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    if (
      selectedDay?.getMonth() === currentMonth.getMonth() &&
      selectedDay?.getFullYear() === currentMonth.getFullYear()
    ) {
      onSelectedDayChange(null);
    }
    if (isActive) onToggle();
  };

  const handleBarClick = (dayDate: Date) => {
    onSelectedDayChange(selectedDay && isSameDay(selectedDay, dayDate) ? null : dayDate);
  };

  return (
    <div className="relative rounded-2xl border bg-white p-4 shadow-[0_1px_0_rgba(0,0,0,0.05)]">
      <ChartHeader
        monthLabel={monthLabel}
        isActive={isActive}
        onToggle={onToggle}
        total={total}
        onPrev={goToPrevMonth}
        onNext={goToNextMonth}
        mode={mode}
      />

      {/* 7 cols on mobile · 16 cols on sm · 31 cols on lg */}
      <div className="grid grid-cols-7 gap-x-1 gap-y-6 px-4 pt-4 sm:grid-cols-[repeat(16,minmax(0,1fr))] lg:grid-cols-[repeat(31,minmax(0,1fr))]">
        {days.map((counts, i) => {
          const day = i + 1;
          const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
          const dayTotal = getModeTotal(counts, mode);
          const fillHeight = dayTotal > 0 ? Math.max(5, (dayTotal / maxTotal) * 100) : 0;
          const showLabel = day === 1 || day % 5 === 0 || day === dayCount;

          return (
            <div key={i} className="h-[147px]">
              <DayBar
                counts={counts}
                total={dayTotal}
                dayDate={dayDate}
                fillHeight={fillHeight}
                isSelected={!!selectedDay && isSameDay(selectedDay, dayDate)}
                isToday={isSameDay(today, dayDate)}
                isHovered={hoveredDay === i}
                showLabel={showLabel}
                onHover={(hovered) => setHoveredDay(hovered ? i : null)}
                onClick={() => handleBarClick(dayDate)}
                mode={mode}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
